/**
 * ButtonFab — Fully assembled Button.
 *
 * Pass label, optional icon, variant, size — colors are resolved internally.
 * For full control, use the primitive Button + ButtonText instead.
 */
import type { LucideIcon } from 'lucide-react-native'
import type { GetProps } from 'tamagui'
import { Button, ButtonText } from '../elements/Button'
import { Spinner } from '../elements/Spinner'
import { XStack } from '../layout'

const TEXT_COLOR: Record<string, string> = {
  primary: '$colorInverse',
  secondary: '$color',
  secondaryoutline: '$color',
  outline: '$colorPrimary',
  destructive: '$colorInverse',
  destructiveoutline: '$colorError',
  ghost: '$color',
}

const ICON_COLOR: Record<string, string> = {
  primary: '$colorInverse',
  secondary: '$color',
  secondaryoutline: '$color',
  outline: '$colorPrimary',
  destructive: '$colorInverse',
  destructiveoutline: '$colorError',
  ghost: '$colorPrimary',
}

const ICON_SIZE: Record<string, number> = {
  sm: 16,
  md: 18,
  lg: 20,
}

type ButtonFrameProps = GetProps<typeof Button>

interface ButtonFabProps extends Omit<ButtonFrameProps, 'children'> {
  label: string
  icon?: LucideIcon
  iconAfter?: LucideIcon
  loading?: boolean
}

export function ButtonFab({
  label,
  icon: Icon,
  iconAfter: IconAfter,
  variant = 'primary',
  size = 'md',
  loading,
  ...props
}: ButtonFabProps) {
  const textColor = TEXT_COLOR[variant] ?? '$colorInverse'
  const iconColor = ICON_COLOR[variant] ?? '$colorInverse'
  const iconSize = ICON_SIZE[size] ?? 18

  return (
    <Button variant={variant} size={size} loading={loading} {...props}>
      {!loading && (
        <>
          {Icon && <Icon size={iconSize} color={iconColor} />}
          <ButtonText color={textColor} size={size}>{label}</ButtonText>
          {IconAfter && <IconAfter size={iconSize} color={iconColor} />}
        </>
      )}
    </Button>
  )
}
